<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Archivos adicionales para la orden</p>
      <button type="button" class="delete" @click="$emit('close')"/>
    </header>

    <section class="modal-card-body">
      <b-loading :active="isLoading" :is-full-page="false" />
      <MediaFinder
          ref="mediaFinder"
          :initial-files="files"
          @upload="handleUpload"
          @delete="handleDelete"
          @rename="handleRename"
          @toggle-share="handleToggleShare"
      />
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <b-button label="Cerrar" @click="$emit('close')"/>
    </footer>
  </div>
</template>

<script>
import MediaFinder from "../MediaFinder.vue";

const MEDIABLE_TYPE = "order"; // morph map alias; use 'App\\Models\\Order' if not mapped

export default {
  name: "ResultsExtraMediaFinder",
  components: { MediaFinder },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      files: [],
      isLoading: false
    };
  },
  created() {
    this.fetchFiles();
  },
  methods: {
    async fetchFiles() {
      this.isLoading = true;
      try {
        const { data } = await this.$http.get("/media-files", {
          params: {
            mediable_type: MEDIABLE_TYPE,
            mediable_id: this.order.id
          }
        });
        this.files = data.map(this.mapServerFile);
      } catch (error) {
        this.notifyError("No se pudieron cargar los archivos.");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Upload each pending file individually so we can report per-file
     * progress and keep failed files in the queue for retry.
     */
    async handleUpload(pendingFiles) {
      const finder = this.$refs.mediaFinder;
      finder.setUploading(true);

      let anySucceeded = false;

      for (const pending of pendingFiles) {
        finder.setPendingStatus(pending.id, "uploading");

        const formData = new FormData();
        formData.append("mediable_type", MEDIABLE_TYPE);
        formData.append("mediable_id", this.order.id);
        formData.append("files[]", pending.file);

        try {
          const { data } = await this.$http.post("/media-files", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (event) => {
              const percent = event.total
                  ? Math.round((event.loaded * 100) / event.total)
                  : 0;
              finder.setPendingProgress(pending.id, percent);
            }
          });

          finder.setPendingProgress(pending.id, 100);
          finder.setPendingStatus(pending.id, "success");

          const mapped = (Array.isArray(data) ? data : [data]).map(this.mapServerFile);
          this.files = [...mapped, ...this.files];
          anySucceeded = true;

          setTimeout(() => finder.resolvePendingFile(pending.id), 600);
        } catch (error) {
          finder.setPendingStatus(pending.id, "error");
          this.notifyError(`No se pudo subir ${pending.name}.`);
          // Left in the queue so the user can remove or retry
        }
      }

      finder.setUploading(false);

      if (anySucceeded) {
        this.$buefy.toast.open({ message: "Carga completada.", type: "is-success" });
      }
    },

    async handleDelete(fileId) {
      try {
        await this.$http.delete(`/media-files/${fileId}`);
        this.files = this.files.filter((item) => item.id !== fileId);
      } catch (error) {
        this.notifyError("No se pudo eliminar el archivo.");
        this.fetchFiles();
      }
    },

    async handleRename({ id, name }) {
      try {
        await this.$http.patch(`/media-files/${id}/rename`, { name });
      } catch (error) {
        this.notifyError("No se pudo renombrar el archivo.");
        this.fetchFiles();
      }
    },

    async handleToggleShare({ id, sharedWithPatient }) {
      try {
        await this.$http.patch(`/media-files/${id}/share`, {
          shared_with_patient: sharedWithPatient
        });
      } catch (error) {
        this.notifyError("No se pudo actualizar el estado de compartido.");
        this.fetchFiles();
      }
    },

    /**
     * Map the backend snake_case payload to the camelCase shape MediaFinder expects.
     */
    mapServerFile(serverFile) {
      return {
        id: serverFile.id,
        name: serverFile.name,
        mimeType: serverFile.mime_type,
        size: serverFile.size,
        uploadedAt: serverFile.created_at,
        url: serverFile.url,
        thumbnailUrl: serverFile.thumbnail_url,
        sharedWithPatient: serverFile.shared_with_patient,
        file: null
      };
    },

    notifyError(message) {
      this.$buefy.toast.open({ message, type: "is-danger" });
    }
  }
};
</script>

<style scoped>
.modal-card-body {
  position: relative;
  min-height: 200px;
}
</style>